import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Secure storage helpers — use sessionStorage (shorter-lived, cleared on tab close)
const STORAGE_PREFIX = 'riq_';
const storage = {
  set: (key, value) => {
    try {
      sessionStorage.setItem(STORAGE_PREFIX + key, typeof value === 'string' ? value : JSON.stringify(value));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Storage set error:', e);
    }
  },
  get: (key) => {
    try {
      return sessionStorage.getItem(STORAGE_PREFIX + key);
    } catch (e) {
      return null;
    }
  },
  getJSON: (key) => {
    try {
      const val = sessionStorage.getItem(STORAGE_PREFIX + key);
      return val ? JSON.parse(val) : null;
    } catch (e) {
      return null;
    }
  },
  remove: (key) => {
    try {
      sessionStorage.removeItem(STORAGE_PREFIX + key);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Storage remove error:', e);
    }
  },
};

export { storage };

const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000, // 60 second timeout for AI-heavy endpoints
  withCredentials: true, // send/receive httpOnly auth cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// Sign-in endpoints report a wrong/expired code as 401 — that's not an expired session.
const SIGN_IN_PATHS = ['/api/auth/request-code', '/api/auth/verify-code', '/api/auth/verify-link', '/api/auth/register', '/api/auth/logout'];
export const SESSION_EXPIRED_EVENT = 'chire:session-expired';

// A 401 only means "session expired" if this tab had a signed-in user. Anonymous visitors on
// public pages are never redirected; AuthContext clears the user, and ProtectedRoute sends
// protected pages to /login exactly once. 401s during that hand-off stay pending (the page is
// unmounting) so they don't raise their own error toasts on top of the session notice.
let expiredUntil = 0;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    if (error.response?.status !== 401 || SIGN_IN_PATHS.some((p) => url.includes(p))) return Promise.reject(error);
    if (storage.get('user')) {
      storage.remove('user');
      expiredUntil = Date.now() + 5000;
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    }
    return Date.now() < expiredUntil ? new Promise(() => {}) : Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  requestCode: (email) => api.post('/api/auth/request-code', { email }),
  verifyCode: (data) => api.post('/api/auth/verify-code', data),
  verifyLink: (token) => api.post('/api/auth/verify-link', { token }),
  register: (data) => api.post('/api/auth/register', data),
  logout: () => api.post('/api/auth/logout'),
  getMe: () => api.get('/api/auth/me'),
};

// Career-page applications inbox (unparsed leads)
export const leadsAPI = {
  list: () => api.get('/api/leads'),
  count: () => api.get('/api/leads/count'),
  dismiss: (id) => api.post(`/api/leads/${id}/dismiss`),
};

// Public (no-auth) endpoints for marketing pages
export const publicAPI = {
  matchingBenchmark: () => api.get('/api/public/matching-benchmark'),
  jobs: (slug) => api.get('/api/public/jobs', { params: slug ? { slug } : {} }),
  careersContext: (slug) => api.get('/api/public/careers-context', { params: slug ? { slug } : {} }),
  job: (id) => api.get(`/api/public/jobs/${id}`),
  applyLead: (id, formData) => api.post(`/api/public/jobs/${id}/apply-lead`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  feedUrl: () => `${API_BASE}/api/public/jobs-feed.xml`,
  assetUrl: (path) => (path ? `${API_BASE}${path}` : null),
};

// Employer careers-page management (company slug + public roles)
export const careersAPI = {
  getConfig: () => api.get('/api/careers/config'),
  setSlug: (slug) => api.put('/api/careers/slug', { slug }),
};

// Employer careers-page branding
export const settingsAPI = {
  getBranding: () => api.get('/api/settings/branding'),
  saveBranding: (data) => api.put('/api/settings/branding', data),
  uploadLogo: (formData) => api.post('/api/settings/branding/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadBanner: (formData) => api.post('/api/settings/branding/banner', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeBrandingAsset: (kind) => api.delete(`/api/settings/branding/asset/${kind}`),
};


// Jobs
export const jobsAPI = {
  list: (params) => api.get('/api/jobs', { params: typeof params === 'string' ? { status: params } : (params || {}) }),
  get: (id) => api.get(`/api/jobs/${id}`),
  create: (data) => api.post('/api/jobs', data),
  update: (id, data) => api.put(`/api/jobs/${id}`, data),
  delete: (id) => api.delete(`/api/jobs/${id}`),
  rescore: (id) => api.post(`/api/jobs/${id}/rescore`),
  retryStuck: (id) => api.post(`/api/jobs/${id}/retry-stuck`),
  scoringInsights: (id) => api.get(`/api/jobs/${id}/scoring-insights`),
  exportCSV: (id) => api.get(`/api/jobs/${id}/export`),
  parseJD: (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/jobs/parse-jd', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
  extractSkills: (text) => api.post('/api/jobs/extract-skills', { text }),
  generateJD: (data) => api.post('/api/jobs/generate-jd', data),
  jdQuality: (data) => api.post('/api/jobs/jd-quality', data),
};

// Sourcing (JD-based candidate sourcing engine)
export const sourcingAPI = {
  getKit: (jobId) => api.get(`/api/jobs/${jobId}/sourcing-kit`),
  generateKit: (jobId) => api.post(`/api/jobs/${jobId}/sourcing-kit`),
  sourceInternal: (jobId) => api.post(`/api/jobs/${jobId}/source-internal`),
  getInternal: (jobId) => api.get(`/api/jobs/${jobId}/source-internal`),
  addSourced: (jobId, data) => api.post(`/api/jobs/${jobId}/source-internal/add`, data),
};

// Candidates
export const candidatesAPI = {
  list: (jobId, sortBy, sortOrder) => api.get(`/api/jobs/${jobId}/candidates`, {
    params: { sort_by: sortBy, sort_order: sortOrder }
  }),
  get: (id) => api.get(`/api/candidates/${id}`),
  reparse: (id) => api.post(`/api/candidates/${id}/reparse`),
  updateDetails: (id, data) => api.put(`/api/candidates/${id}/details`, data),
  matchFeedback: (id, data) => api.post(`/api/candidates/${id}/match-feedback`, data),
  uploadResume: (jobId, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/jobs/${jobId}/upload-resume`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
  uploadMultiple: (jobId, files, onUploadProgress) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post(`/api/jobs/${jobId}/upload-resumes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
};

// Assessments
export const assessmentsAPI = {
  generate: (jobId, numQuestions = 10) => api.post(`/api/jobs/${jobId}/generate-assessment?num_questions=${numQuestions}`),
  sendToCandidate: (jobId, data) => api.post(`/api/jobs/${jobId}/send-assessment-to-candidate`, data),
  listForJob: (jobId) => api.get(`/api/jobs/${jobId}/assessments`),
  listAll: () => api.get('/api/assessments'),
  get: (id) => api.get(`/api/assessments/${id}`),
  getByCode: (code) => api.get(`/api/assessments/by-code/${code}`),
  startAttempt: (assessmentId, data) => api.post(`/api/assessments/${assessmentId}/start`, data),
  saveAnswer: (attemptId, data) => api.put(`/api/assessment-attempts/${attemptId}/answer`, data),
  logAntiCheat: (attemptId, data) => api.post(`/api/assessment-attempts/${attemptId}/anti-cheat`, data),
  submitAttempt: (attemptId) => api.post(`/api/assessment-attempts/${attemptId}/submit`),
  runCode: (attemptId, data) => api.post(`/api/assessment-attempts/${attemptId}/run-code`, data),
  getAttempt: (attemptId) => api.get(`/api/assessment-attempts/${attemptId}`),
  listAttempts: (assessmentId) => api.get(`/api/assessments/${assessmentId}/attempts`),
};

// Interviews
export const interviewsAPI = {
  getTemplates: () => api.get('/api/interview-templates'),
  create: (jobId, data) => api.post(`/api/jobs/${jobId}/create-interview`, data),
  sendToCandidate: (jobId, data) => api.post(`/api/jobs/${jobId}/send-interview-to-candidate`, data),
  listForJob: (jobId) => api.get(`/api/jobs/${jobId}/interviews`),
  listAll: () => api.get('/api/interviews'),
  get: (id) => api.get(`/api/interviews/${id}`),
  getByCode: (code) => api.get(`/api/interviews/by-code/${code}`),
  startSession: (interviewId, data) => api.post(`/api/interviews/${interviewId}/start-session`, data),
  saveTranscript: (sessionId, data) => api.put(`/api/interview-sessions/${sessionId}/transcript`, data),
  logAntiCheat: (sessionId, data) => api.post(`/api/interview-sessions/${sessionId}/anti-cheat`, data),
  completeSession: (sessionId) => api.post(`/api/interview-sessions/${sessionId}/complete`),
  getSession: (sessionId) => api.get(`/api/interview-sessions/${sessionId}`),
  listSessions: (interviewId) => api.get(`/api/interviews/${interviewId}/sessions`),
  requestDataDeletion: (data) => api.post('/api/interview/request-data-deletion', data),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/api/dashboard/stats'),
  getCommandCenter: () => api.get('/api/dashboard/recruiter-command-center'),
  getHiringTrends: (weeks = 8) => api.get(`/api/dashboard/hiring-trends?weeks=${weeks}`),
};

// Applications (recruiter/admin)
export const applicationsAPI = {
  listAll: () => api.get('/api/applications'),
};

// Admin
export const adminAPI = {
  runSecurityCheck: () => api.post('/api/admin/security-check'),
  latestSecurityCheck: () => api.get('/api/admin/security-check/latest'),
  getUsers: () => api.get('/api/admin/users'),
  createUser: (data) => api.post('/api/admin/users', data),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  getStats: () => api.get('/api/admin/stats'),
  getSelfAssessmentInvoices: () => api.get('/api/admin/self-assessment-invoices'),
  getScoringAccuracy: () => api.get('/api/admin/scoring-accuracy'),
  getAiUsage: (month) => api.get('/api/admin/ai-usage', { params: month ? { month } : {} }),
  updateAiUsageConfig: (data) => api.put('/api/admin/ai-usage/config', data),
  getAiUsageByLogin: (month) => api.get('/api/admin/ai-usage/by-login', { params: month ? { month } : {} }),
  getAiUsageLoginTimeline: (recruiterId, month) => api.get(`/api/admin/ai-usage/login/${recruiterId}`, { params: month ? { month } : {} }),
  backfillUsageLinks: () => api.post('/api/admin/ai-usage/backfill-links'),
  getAiUsageByJob: (month) => api.get('/api/admin/ai-usage/by-job', { params: month ? { month } : {} }),
  getLearning: () => api.get('/api/admin/learning'),
  recomputeLearning: () => api.post('/api/admin/learning/recompute'),
  getAuditLogs: () => api.get('/api/admin/audit-logs'),
  getDomainRules: () => api.get('/api/admin/domain-rules'),
  addDomainRule: (data) => api.post('/api/admin/domain-rules', data),
  deleteDomainRule: (id) => api.delete(`/api/admin/domain-rules/${id}`),
};

// Company-scoped credit usage (employer team view)
export const usageAPI = {
  team: (month) => api.get('/api/usage/team', { params: month ? { month } : {} }),
  teamLoginTimeline: (recruiterId, month) => api.get(`/api/usage/team/login/${recruiterId}`, { params: month ? { month } : {} }),
  teamByJob: (month) => api.get('/api/usage/team/by-job', { params: month ? { month } : {} }),
};

// Candidate portal
export const candidateAPI = {
  getProfile: () => api.get('/api/candidate/profile'),
  getJobs: () => api.get('/api/candidate/jobs'),
  getApplications: () => api.get('/api/candidate/applications'),
  getApplicationDetails: (jobId) => api.get(`/api/candidate/applications/${jobId}/details`),
  getNotifications: () => api.get('/api/candidate/notifications'),
  markNotificationRead: (id) => api.put(`/api/candidate/notifications/${id}/read`),
  apply: (jobId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/candidate/apply/${jobId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Background Verification
export const verificationAPI = {
  generateRequirements: (jobId) => api.post(`/api/jobs/${jobId}/generate-requirements`),
  getRequirements: (jobId) => api.get(`/api/jobs/${jobId}/requirements`),
  updateRequirements: (jobId, data) => api.put(`/api/jobs/${jobId}/requirements`, data),
  createRequest: (data) => api.post('/api/verification-requests', data),
  listRequests: (jobId) => api.get(`/api/jobs/${jobId}/verification-requests`),
  listRequestDocuments: (requestId) => api.get(`/api/verification-requests/${requestId}/documents`),
  getByCode: (code) => api.get(`/api/verification/by-code/${code}`),
  uploadDocument: (accessCode, file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    return api.post(`/api/verification/${accessCode}/upload-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  reviewDocument: (docId, data) => api.put(`/api/candidate-documents/${docId}/review`, data),
  sendReminder: (requestId) => api.post(`/api/verification-requests/${requestId}/send-reminder`),
  getDashboard: () => api.get('/api/verification-dashboard'),
  getBgvUsage: () => api.get('/api/verification/bgv-usage'),
};

// Credential Tokens (NFT)
export const credentialsAPI = {
  issue: (data) => api.post('/api/credentials/issue', data),
  get: (tokenId) => api.get(`/api/credentials/${tokenId}`),
  getForCandidate: (email) => api.get(`/api/credentials/candidate/${email}`),
  listAll: () => api.get('/api/credentials'),
};

// Resume Intelligence + Adaptive Interview + Bias-Free Scoring + Fraud Prevention
export const intelligenceAPI = {
  analyzeDepth: (candidateId) => api.post(`/api/candidates/${candidateId}/analyze-depth`),
  listResumes: () => api.get('/api/resume-intelligence'),
  createAdaptiveInterview: (jobId) => api.post(`/api/jobs/${jobId}/create-adaptive-interview`),
  getAdaptiveFollowup: (sessionId, data) => api.post(`/api/interview-sessions/${sessionId}/adaptive-followup`, data),
  generateRubric: (jobId) => api.post(`/api/jobs/${jobId}/generate-rubric`),
  scoreBiasFree: (data) => api.post('/api/score-bias-free', data),
  // Fraud Prevention
  analyzeApplicationFraud: (data) => api.post('/api/fraud/analyze-application', data),
  submitBiometrics: (data) => api.post('/api/fraud/submit-biometrics', data),
  verifyIdentity: (data) => api.post('/api/fraud/verify-identity', data),
  getFraudReport: (email) => api.get(`/api/fraud/candidate-report/${email}`),
  getFraudDashboard: () => api.get('/api/fraud/dashboard'),
};

// Offers & Pipeline
export const offersAPI = {
  create: (data) => api.post('/api/offers', data),
  update: (id, data) => api.put(`/api/offers/${id}`, data),
  list: () => api.get('/api/offers'),
  getForCandidate: (candidateId) => api.get(`/api/candidates/${candidateId}/offer`),
  generateOnboarding: (offerId) => api.post(`/api/offers/${offerId}/onboarding-plan`),
  updateStage: (candidateId, data) => api.post(`/api/candidates/${candidateId}/update-stage`, data),
  getPipelineHistory: (candidateId) => api.get(`/api/candidates/${candidateId}/pipeline-history`),
  getOfferProbability: (candidateId) => api.get(`/api/candidates/${candidateId}/offer-probability`),
};

export const paymentsAPI = {
  createOrder: (plan_key) => api.post('/api/payments/create-order', { plan_key }),
  createScreeningOrder: (quantity) => api.post('/api/payments/create-screening-order', { quantity }),
  createCreditOrder: (packs) => api.post('/api/payments/create-credit-order', { packs }),
  createBgvOrder: (quantity) => api.post('/api/payments/create-bgv-order', { quantity }),
  verify: (data) => api.post('/api/payments/verify', data),
  getSubscription: () => api.get('/api/payments/me/subscription'),
  getHistory: () => api.get('/api/payments/me/history'),
  downloadInvoice: (orderId) => api.get(`/api/payments/${orderId}/invoice`, { responseType: 'blob' }),
  resendInvoice: (orderId) => api.post(`/api/payments/${orderId}/resend-invoice`),
  markFailed: (data) => api.post('/api/payments/failed', data),
  // Stripe (USD) checkout
  stripeCreatePlanSession: (plan_key, origin_url) => api.post('/api/payments/stripe/create-plan-session', { plan_key, origin_url }),
  stripeCreateCreditSession: (packs, origin_url) => api.post('/api/payments/stripe/create-credit-session', { packs, origin_url }),
  stripeCreateBgvSession: (quantity, origin_url) => api.post('/api/payments/stripe/create-bgv-session', { quantity, origin_url }),
  stripeStatus: (sessionId) => api.get(`/api/payments/stripe/status/${sessionId}`),
};

// Candidate compliance requests (ADA accommodation + NYC LL144 AEDT alternative)
export const complianceAPI = {
  submitCandidateRequest: (data) => api.post('/api/candidate-requests', data),
  getCandidateRequests: (category, status) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    const qs = params.toString();
    return api.get(`/api/admin/candidate-requests${qs ? `?${qs}` : ''}`);
  },
  updateCandidateRequest: (id, data) => api.patch(`/api/admin/candidate-requests/${id}`, data),
  // Bias audit summaries (NYC LL144)
  getPublicBiasAudit: () => api.get('/api/bias-audit'),
  listBiasAudits: () => api.get('/api/admin/bias-audits'),
  createBiasAudit: (formData) => api.post('/api/admin/bias-audits', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteBiasAudit: (id) => api.delete(`/api/admin/bias-audits/${id}`),
};

export const privacyAPI = {
  submitDataRequest: (data) => api.post('/api/data-requests', data),
  logConsent: (data) => api.post('/api/consent-logs', data),
  getConsentLogs: (limit = 500) => api.get(`/api/admin/consent-logs?limit=${limit}`),
  getDataRequests: (status) => api.get(`/api/admin/data-requests${status ? `?status=${status}` : ''}`),
  updateDataRequest: (id, data) => api.patch(`/api/admin/data-requests/${id}`, data),
};

// Behavioral / Personality Assessment (DISC-style, standalone module)
export const behavioralAPI = {
  // recruiter
  invite: (data) => api.post('/api/behavioral/invites', data),
  listSessions: () => api.get('/api/behavioral/sessions'),
  getSession: (id) => api.get(`/api/behavioral/sessions/${id}`),
  compare: (sessionIds) => api.post('/api/behavioral/compare', { session_ids: sessionIds }),
  resend: (id) => api.post(`/api/behavioral/sessions/${id}/resend`),
  remove: (id) => api.delete(`/api/behavioral/sessions/${id}`),
  reportPdfUrl: (id) => `${API_BASE}/api/behavioral/sessions/${id}/report.pdf`,
  // role fit & team chemistry (job-scoped)
  archetypes: () => api.get('/api/behavioral/archetypes'),
  setJobTarget: (jobId, data) => api.put(`/api/behavioral/jobs/${jobId}/target`, data),
  jobFit: (jobId) => api.get(`/api/behavioral/jobs/${jobId}/fit`),
  suggestTarget: (jobId) => api.get(`/api/behavioral/jobs/${jobId}/suggest-target`),
  traitSearch: (trait, excludeJobId) => api.get('/api/behavioral/trait-search', { params: { trait, exclude_job_id: excludeJobId } }),
  // public (candidate, token-based)
  take: (token) => api.get(`/api/behavioral/take/${token}`),
  takeStart: (token) => api.post(`/api/behavioral/take/${token}/start`),
  takeResponse: (token, data) => api.post(`/api/behavioral/take/${token}/response`, data),
  takeSubmit: (token) => api.post(`/api/behavioral/take/${token}/submit`),
  takeResults: (token) => api.get(`/api/behavioral/take/${token}/results`),
};

// Self-serve behavioral assessment (public, paid, no account)
export const selfAssessmentAPI = {
  config: () => api.get('/api/self-assessment/config'),
  requestCode: (email) => api.post('/api/self-assessment/request-code', { email }),
  verifyCode: (email, code) => api.post('/api/self-assessment/verify-code', { email, code }),
  createOrder: (data) => api.post('/api/self-assessment/create-order', data),
  verifyPayment: (data) => api.post('/api/self-assessment/verify-payment', data),
  myReports: (intake_id) => api.post('/api/self-assessment/my-reports', { intake_id }),
  resendReport: (intake_id, session_id) => api.post('/api/self-assessment/resend-report', { intake_id, session_id }),
  downloadReport: (session_id, intake_id, kind) => api.get(`/api/self-assessment/report/${session_id}`, { params: { intake_id, kind }, responseType: 'blob' }),
};

export default api;
